import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Settings,
  Eye,
  Save,
} from 'lucide-react';
import { api } from '@/lib/api';
import type { GeneralRequestForm, FormFieldType, Allergen, VenueArea } from '@/types';

const FIELD_TYPES: { value: FormFieldType; label: string; description: string }[] = [
  { value: 'text', label: 'Text', description: 'Single line text input' },
  { value: 'textarea', label: 'Text Area', description: 'Multi-line text input' },
  { value: 'number', label: 'Number', description: 'Numeric input' },
  { value: 'email', label: 'Email', description: 'Email with validation' },
  { value: 'phone', label: 'Phone', description: 'UK phone number' },
  { value: 'mobile', label: 'Mobile', description: 'UK mobile number' },
  { value: 'postcode', label: 'Postcode', description: 'UK postcode' },
  { value: 'date', label: 'Date', description: 'Date picker' },
  { value: 'time', label: 'Time', description: 'Time picker' },
  { value: 'datetime', label: 'Date & Time', description: 'Date and time picker' },
  { value: 'dropdown', label: 'Dropdown', description: 'Single selection dropdown' },
  { value: 'radio', label: 'Radio Buttons', description: 'Single selection options' },
  { value: 'checkbox', label: 'Checkbox', description: 'Yes/No checkbox' },
  { value: 'checkbox_group', label: 'Checkbox Group', description: 'Multiple selection' },
  { value: 'venue_date', label: 'Venue Date', description: 'Linked to venue opening dates' },
  { value: 'venue_area', label: 'Venue Area', description: 'Linked to venue areas' },
  { value: 'venue_time', label: 'Venue Time', description: 'Linked to area/date hours' },
  { value: 'guest_count', label: 'Guest Count', description: 'Number of guests (min/max)' },
  { value: 'allergens', label: 'Allergens', description: 'Allergen checklist' },
];

const formSchema = z.object({
  name: z.string().min(1, 'Form name is required'),
  description: z.string().optional(),
  introText: z.string().optional(),
  successMessage: z.string().optional(),
  requiresApproval: z.boolean(),
  sendConfirmationEmail: z.boolean(),
  isActive: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

interface FieldFormData {
  id?: string;
  tempId: string; // For tracking in UI before save
  field_type: FormFieldType;
  label: string;
  placeholder?: string;
  help_text?: string;
  is_required: boolean;
  config: {
    min?: number;
    max?: number;
    rows?: number;
    linkedToArea?: boolean;
    linkedToDate?: boolean;
  };
  options?: { label: string; value: string }[];
  sort_order: number;
  is_active: boolean;
}

// Sortable field component
function SortableField({
  field,
  index,
  expandedField,
  setExpandedField,
  updateField,
  removeField,
  addOption,
  updateOption,
  removeOption,
}: {
  field: FieldFormData;
  index: number;
  expandedField: string | null;
  setExpandedField: (id: string | null) => void;
  updateField: (index: number, updates: Partial<FieldFormData>) => void;
  removeField: (index: number) => void;
  addOption: (fieldIndex: number) => void;
  updateOption: (fieldIndex: number, optionIndex: number, updates: { label?: string; value?: string }) => void;
  removeOption: (fieldIndex: number, optionIndex: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.tempId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const needsOptions = (type: FormFieldType) => {
    return ['dropdown', 'radio', 'checkbox_group'].includes(type);
  };

  const isExpanded = expandedField === field.tempId;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg ${
        isExpanded ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-200'
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex items-center gap-2 p-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </button>
        <div
          className="flex-1 cursor-pointer"
          onClick={() => setExpandedField(isExpanded ? null : field.tempId)}
        >
          <span className="font-medium text-sm">{field.label}</span>
          <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
            {FIELD_TYPES.find((t) => t.value === field.field_type)?.label}
          </span>
          {field.is_required && (
            <span className="ml-1 text-xs text-red-500">*</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeField(index);
            }}
            className="p-1 hover:bg-red-100 rounded text-red-500"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Label *
              </label>
              <input
                type="text"
                value={field.label}
                onChange={(e) => updateField(index, { label: e.target.value })}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Placeholder
              </label>
              <input
                type="text"
                value={field.placeholder || ''}
                onChange={(e) => updateField(index, { placeholder: e.target.value })}
                className="input text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Help Text
            </label>
            <input
              type="text"
              value={field.help_text || ''}
              onChange={(e) => updateField(index, { help_text: e.target.value })}
              className="input text-sm"
              placeholder="Additional instructions for this field"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={field.is_required}
                onChange={(e) => updateField(index, { is_required: e.target.checked })}
                className="rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm text-gray-700">Required</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={field.is_active}
                onChange={(e) => updateField(index, { is_active: e.target.checked })}
                className="rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>

          {/* Field-specific config */}
          {(field.field_type === 'number' || field.field_type === 'guest_count') && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Minimum
                </label>
                <input
                  type="number"
                  value={field.config?.min || ''}
                  onChange={(e) =>
                    updateField(index, {
                      config: { ...field.config, min: parseInt(e.target.value) || undefined },
                    })
                  }
                  className="input text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Maximum
                </label>
                <input
                  type="number"
                  value={field.config?.max || ''}
                  onChange={(e) =>
                    updateField(index, {
                      config: { ...field.config, max: parseInt(e.target.value) || undefined },
                    })
                  }
                  className="input text-sm"
                />
              </div>
            </div>
          )}

          {field.field_type === 'textarea' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Rows
              </label>
              <input
                type="number"
                value={field.config?.rows || 4}
                onChange={(e) =>
                  updateField(index, {
                    config: { ...field.config, rows: parseInt(e.target.value) || 4 },
                  })
                }
                className="input text-sm w-24"
                min={2}
                max={10}
              />
            </div>
          )}

          {field.field_type === 'venue_time' && (
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={field.config?.linkedToArea || false}
                  onChange={(e) =>
                    updateField(index, {
                      config: { ...field.config, linkedToArea: e.target.checked },
                    })
                  }
                  className="rounded border-gray-300 text-indigo-600"
                />
                <span className="text-sm text-gray-700">Link to Area field</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={field.config?.linkedToDate || false}
                  onChange={(e) =>
                    updateField(index, {
                      config: { ...field.config, linkedToDate: e.target.checked },
                    })
                  }
                  className="rounded border-gray-300 text-indigo-600"
                />
                <span className="text-sm text-gray-700">Link to Date field</span>
              </label>
            </div>
          )}

          {/* Options for dropdown, radio, checkbox_group */}
          {needsOptions(field.field_type) && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700">Options</label>
                <button
                  type="button"
                  onClick={() => addOption(index)}
                  className="text-xs text-indigo-600 hover:text-indigo-800"
                >
                  + Add Option
                </button>
              </div>
              <div className="space-y-2">
                {(field.options || []).map((option, optIdx) => (
                  <div key={optIdx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => updateOption(index, optIdx, { label: e.target.value })}
                      className="input text-sm flex-1"
                      placeholder="Label"
                    />
                    <input
                      type="text"
                      value={option.value}
                      onChange={(e) => updateOption(index, optIdx, { value: e.target.value })}
                      className="input text-sm w-32"
                      placeholder="Value"
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(index, optIdx)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FormBuilder() {
  const { venueId, formId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!formId && formId !== 'new';

  const [fields, setFields] = useState<FieldFormData[]>([]);
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: existingForm, isLoading } = useQuery<GeneralRequestForm>({
    queryKey: ['form', formId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/forms/${formId}`);
      return response.data;
    },
    enabled: isEditing,
  });

  const { data: allergens } = useQuery<Allergen[]>({
    queryKey: ['allergens'],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/forms/allergens`);
      return response.data;
    },
  });

  const { data: areas } = useQuery<VenueArea[]>({
    queryKey: ['venue-areas', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas`);
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      introText: '',
      successMessage: 'Thank you for your submission. We will be in touch shortly.',
      requiresApproval: true,
      sendConfirmationEmail: true,
      isActive: true,
    },
  });

  useEffect(() => {
    if (existingForm) {
      reset({
        name: existingForm.name,
        description: existingForm.description || '',
        introText: existingForm.intro_text || '',
        successMessage: existingForm.success_message || '',
        requiresApproval: existingForm.requires_approval,
        sendConfirmationEmail: existingForm.send_confirmation_email,
        isActive: existingForm.is_active,
      });
      if (existingForm.fields) {
        setFields(
          existingForm.fields.map((f, idx) => ({
            id: f.id,
            tempId: f.id || `temp-${idx}`,
            field_type: f.field_type,
            label: f.label,
            placeholder: f.placeholder || '',
            help_text: f.help_text || '',
            is_required: f.is_required,
            config: f.config || {},
            options: f.options?.map((o) => ({ label: o.label, value: o.value })) || [],
            sort_order: f.sort_order,
            is_active: f.is_active,
          }))
        );
      }
    }
  }, [existingForm, reset]);

  const addField = (type: FormFieldType) => {
    const typeInfo = FIELD_TYPES.find((t) => t.value === type);
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newField: FieldFormData = {
      tempId,
      field_type: type,
      label: typeInfo?.label || 'New Field',
      placeholder: '',
      help_text: '',
      is_required: false,
      config: {},
      options: [],
      sort_order: fields.length + 1,
      is_active: true,
    };

    // Set default config based on type
    if (type === 'guest_count') {
      newField.config = { min: 1, max: 20 };
    } else if (type === 'textarea') {
      newField.config = { rows: 4 };
    } else if (type === 'venue_time') {
      newField.config = { linkedToArea: true, linkedToDate: true };
    }

    setFields([...fields, newField]);
    setExpandedField(tempId);
  };

  const removeField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
    setExpandedField(null);
  };

  const updateField = (index: number, updates: Partial<FieldFormData>) => {
    setFields(
      fields.map((f, i) => (i === index ? { ...f, ...updates } : f))
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFields((items) => {
        const oldIndex = items.findIndex((item) => item.tempId === active.id);
        const newIndex = items.findIndex((item) => item.tempId === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addOption = (fieldIndex: number) => {
    const field = fields[fieldIndex];
    const options = field.options || [];
    updateField(fieldIndex, {
      options: [...options, { label: `Option ${options.length + 1}`, value: `option_${options.length + 1}` }],
    });
  };

  const updateOption = (fieldIndex: number, optionIndex: number, updates: { label?: string; value?: string }) => {
    const field = fields[fieldIndex];
    const options = [...(field.options || [])];
    options[optionIndex] = { ...options[optionIndex], ...updates };
    updateField(fieldIndex, { options });
  };

  const removeOption = (fieldIndex: number, optionIndex: number) => {
    const field = fields[fieldIndex];
    const options = (field.options || []).filter((_, i) => i !== optionIndex);
    updateField(fieldIndex, { options });
  };

  const onSubmit = async (data: FormData) => {
    setIsSaving(true);
    setSaveError(null);

    try {
      let savedFormId = formId;

      // Step 1: Save or create the form
      if (isEditing) {
        await api.put(`/admin/venues/${venueId}/forms/${formId}`, data);
      } else {
        const response = await api.post(`/admin/venues/${venueId}/forms`, data);
        savedFormId = response.data.id;
      }

      // Step 2: Get existing fields to determine what to create/update/delete
      const existingFieldIds = new Set(
        (existingForm?.fields || []).map((f) => f.id)
      );
      const currentFieldIds = new Set(
        fields.filter((f) => f.id).map((f) => f.id)
      );

      // Delete fields that were removed
      for (const existingField of existingForm?.fields || []) {
        if (!currentFieldIds.has(existingField.id)) {
          await api.delete(`/admin/venues/${venueId}/forms/${savedFormId}/fields/${existingField.id}`);
        }
      }

      // Create or update fields
      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        const fieldPayload = {
          fieldType: field.field_type,
          label: field.label,
          placeholder: field.placeholder || null,
          helpText: field.help_text || null,
          isRequired: field.is_required,
          config: field.config,
          sortOrder: i + 1,
          isActive: field.is_active,
          options: field.options,
        };

        if (field.id && existingFieldIds.has(field.id)) {
          // Update existing field
          await api.put(
            `/admin/venues/${venueId}/forms/${savedFormId}/fields/${field.id}`,
            fieldPayload
          );
        } else {
          // Create new field
          await api.post(
            `/admin/venues/${venueId}/forms/${savedFormId}/fields`,
            fieldPayload
          );
        }
      }

      queryClient.invalidateQueries({ queryKey: ['venue-forms', venueId] });
      queryClient.invalidateQueries({ queryKey: ['form', formId] });
      navigate(`/venues/${venueId}/forms`);
    } catch (error: any) {
      console.error('Save error:', error);
      setSaveError(error.response?.data?.message || 'Failed to save form. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/forms`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Form' : 'Create Form'}
          </h1>
          <p className="text-sm text-gray-500">
            {(venue as Record<string, unknown>)?.name as string}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowSettings(!showSettings)}
          className={`btn ${showSettings ? 'btn-primary' : 'btn-secondary'}`}
        >
          <Settings className="h-4 w-4 mr-2" />
          Settings
        </button>
        <button
          onClick={handleSubmit(onSubmit)}
          disabled={isSaving}
          className="btn btn-primary"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Form'}
        </button>
      </div>

      {saveError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {saveError}
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">Form Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Form Name *
              </label>
              <input
                type="text"
                {...register('name')}
                className="input"
                placeholder="e.g., Table Reservation Request"
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                {...register('description')}
                className="input"
                placeholder="Brief description of this form"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Introduction Text
              </label>
              <textarea
                {...register('introText')}
                className="input"
                rows={2}
                placeholder="Text shown at the top of the form"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Success Message
              </label>
              <textarea
                {...register('successMessage')}
                className="input"
                rows={2}
                placeholder="Message shown after successful submission"
              />
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('requiresApproval')}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Requires approval</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('sendConfirmationEmail')}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Send confirmation email</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Builder Panel */}
        <div className="card h-fit">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Form Fields</h2>
            <div className="relative group">
              <button className="btn btn-secondary">
                <Plus className="h-4 w-4 mr-2" />
                Add Field
              </button>
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 max-h-96 overflow-y-auto">
                {FIELD_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => addField(type.value)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                  >
                    <span className="font-medium text-sm">{type.label}</span>
                    <span className="block text-xs text-gray-500">{type.description}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {fields.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No fields added yet.</p>
              <p className="text-sm">Click "Add Field" to start building your form.</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map((f) => f.tempId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <SortableField
                      key={field.tempId}
                      field={field}
                      index={index}
                      expandedField={expandedField}
                      setExpandedField={setExpandedField}
                      updateField={updateField}
                      removeField={removeField}
                      addOption={addOption}
                      updateOption={updateOption}
                      removeOption={removeOption}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Live Preview Panel */}
        <div className="card h-fit sticky top-4">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-semibold">Live Preview</h2>
          </div>

          <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
            <form className="space-y-4">
              {fields.filter((f) => f.is_active).length === 0 ? (
                <p className="text-center text-gray-400 py-8">
                  Add fields to see the preview
                </p>
              ) : (
                fields
                  .filter((f) => f.is_active)
                  .map((field) => (
                    <div key={field.tempId}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                        {field.is_required && <span className="text-red-500 ml-1">*</span>}
                      </label>

                      {/* Text */}
                      {field.field_type === 'text' && (
                        <input
                          type="text"
                          placeholder={field.placeholder}
                          className="input"
                          disabled
                        />
                      )}

                      {/* Textarea */}
                      {field.field_type === 'textarea' && (
                        <textarea
                          placeholder={field.placeholder}
                          rows={field.config?.rows || 4}
                          className="input"
                          disabled
                        />
                      )}

                      {/* Number / Guest Count */}
                      {(field.field_type === 'number' || field.field_type === 'guest_count') && (
                        <input
                          type="number"
                          placeholder={field.placeholder}
                          min={field.config?.min}
                          max={field.config?.max}
                          className="input"
                          disabled
                        />
                      )}

                      {/* Email */}
                      {field.field_type === 'email' && (
                        <input
                          type="email"
                          placeholder={field.placeholder || 'email@example.com'}
                          className="input"
                          disabled
                        />
                      )}

                      {/* Phone / Mobile */}
                      {(field.field_type === 'phone' || field.field_type === 'mobile') && (
                        <input
                          type="tel"
                          placeholder={field.placeholder || (field.field_type === 'mobile' ? '07xxx xxxxxx' : '01xxx xxxxxx')}
                          className="input"
                          disabled
                        />
                      )}

                      {/* Postcode */}
                      {field.field_type === 'postcode' && (
                        <input
                          type="text"
                          placeholder={field.placeholder || 'SW1A 1AA'}
                          className="input"
                          disabled
                        />
                      )}

                      {/* Date */}
                      {(field.field_type === 'date' || field.field_type === 'venue_date') && (
                        <input type="date" className="input" disabled />
                      )}

                      {/* Time / Venue Time */}
                      {(field.field_type === 'time' || field.field_type === 'venue_time') && (
                        <input type="time" className="input" disabled />
                      )}

                      {/* DateTime */}
                      {field.field_type === 'datetime' && (
                        <input type="datetime-local" className="input" disabled />
                      )}

                      {/* Dropdown / Venue Area */}
                      {(field.field_type === 'dropdown' || field.field_type === 'venue_area') && (
                        <select className="input" disabled>
                          <option value="">Select an option...</option>
                          {field.field_type === 'venue_area'
                            ? areas?.map((area) => (
                                <option key={area.id} value={area.id}>
                                  {area.name}
                                </option>
                              ))
                            : field.options?.map((opt, i) => (
                                <option key={i} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                        </select>
                      )}

                      {/* Radio */}
                      {field.field_type === 'radio' && (
                        <div className="space-y-2">
                          {(field.options?.length ? field.options : [{ label: 'Option 1', value: '1' }]).map(
                            (opt, i) => (
                              <label key={i} className="flex items-center gap-2">
                                <input type="radio" name={`preview_${field.tempId}`} disabled />
                                <span className="text-sm">{opt.label}</span>
                              </label>
                            )
                          )}
                        </div>
                      )}

                      {/* Checkbox */}
                      {field.field_type === 'checkbox' && (
                        <label className="flex items-center gap-2">
                          <input type="checkbox" disabled className="rounded" />
                          <span className="text-sm">{field.label}</span>
                        </label>
                      )}

                      {/* Checkbox Group */}
                      {field.field_type === 'checkbox_group' && (
                        <div className="space-y-2">
                          {(field.options?.length ? field.options : [{ label: 'Option 1', value: '1' }]).map(
                            (opt, i) => (
                              <label key={i} className="flex items-center gap-2">
                                <input type="checkbox" disabled className="rounded" />
                                <span className="text-sm">{opt.label}</span>
                              </label>
                            )
                          )}
                        </div>
                      )}

                      {/* Allergens */}
                      {field.field_type === 'allergens' && (
                        <div className="grid grid-cols-2 gap-2">
                          {allergens?.slice(0, 6).map((allergen) => (
                            <label key={allergen.id} className="flex items-center gap-2">
                              <input type="checkbox" disabled className="rounded" />
                              <span className="text-sm">{allergen.name}</span>
                            </label>
                          ))}
                          {allergens && allergens.length > 6 && (
                            <span className="text-xs text-gray-500 col-span-2">
                              + {allergens.length - 6} more allergens
                            </span>
                          )}
                        </div>
                      )}

                      {field.help_text && (
                        <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
                      )}
                    </div>
                  ))
              )}

              {fields.filter((f) => f.is_active).length > 0 && (
                <button
                  type="button"
                  className="w-full btn btn-primary mt-6"
                  disabled
                >
                  Submit Request
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
